import { MultiComboBox } from "@/components/shared/MultiComboBox/MultiComboBox";
import PaymentMethodSelector from "@/components/shared/PaymentMethodSelector/PaymentMethodSelector";
import StatusSelector from "@/components/shared/StatusSelector/StatusSelector";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SellsService } from "@/services/sells.service";
import useCurrencyStore from "@/store/useCurrencyStore";
import useModalStore from "@/store/useModalStore";
import { useSoldProductFilterOptions } from "@/hooks/useSoldProductFilterOptions";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { PAYMENT_STATUS, PROCESSING_STATUS } from "../hooks/useOrders";

export const FILTER_ORDERS_MODAL = "filter-orders";

// Schema de validação do formulário de filtros
const FilterSchema = z.object({
  products: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  offers: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  affiliates: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  payment_methods: z.array(z.string()).optional(),
  status: z.array(z.string()).optional(),
  processing_status: z.array(z.string()).optional(),
});

type FilterFormData = z.infer<typeof FilterSchema>;

interface FilterOrdersProps {
  filterValues: FilterFormData;
  onFilter: (data: FilterFormData) => void;
}

export default function FilterOrders({
  onFilter,
  filterValues,
}: FilterOrdersProps) {
  const { t } = useTranslation(["orders", "common", "sales"]);
  const { closeModal } = useModalStore();
  const { currency } = useCurrencyStore();

  const { data: soldPaymentMethods = [] } = useQuery({
    queryKey: ["sold-payment-methods", currency],
    queryFn: () => SellsService.getSoldPaymentMethods(currency),
    staleTime: 60_000,
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    getValues,
  } = useForm<FilterFormData>({
    shouldUnregister: false,
    resolver: zodResolver(FilterSchema),
    defaultValues: {
      products: filterValues?.products || [],
      offers: filterValues?.offers || [],
      affiliates: filterValues?.affiliates || [],
      payment_methods: filterValues?.payment_methods || [],
      status: filterValues?.status || [],
      processing_status: filterValues?.processing_status || [],
    },
  });

  const defaultValues = watch();

  useEffect(() => {
    if (filterValues?.payment_methods?.length) return;
    if (soldPaymentMethods.length === 0) return;
    const types = soldPaymentMethods.map(m => m.type);
    const current = watch("payment_methods") ?? [];
    const same =
      current.length === types.length &&
      types.every(t => current.includes(t));
    if (!same) {
      setValue("payment_methods", types);
    }
  }, [soldPaymentMethods, filterValues?.payment_methods]);

  const {
    productIds,
    productIdsKey,
    fetchProductOptions,
    fetchOfferOptions,
    fetchAffiliatesOptions,
    handleProductsChange,
    handleOffersChange,
    queryKeys,
  } = useSoldProductFilterOptions({
    watch,
    setValue,
    getValues,
    currency,
    scope: "orders",
    enableAffiliates: true,
    enableCoproducers: false,
  });

  const onSubmit = async (data: FilterFormData) => {
    onFilter(data);
    closeModal(FILTER_ORDERS_MODAL);
  };

  const handleSelectAll = <T extends keyof FilterFormData>(
    field: T,
    items: FilterFormData[T]
  ) => {
    const currentValues = defaultValues[field] ?? [];
    const isSelectedAll =
      Array.isArray(currentValues) && currentValues.length === (items as unknown[]).length;

    setValue(field, isSelectedAll ? ([] as any) : items);
  };



  return (
    <section className="h-full flex relative flex-col justify-between">
      <SheetHeader className="md:p-6 p-4">
        <SheetTitle className="flex gap-2">{t("sales:filters")}</SheetTitle>
      </SheetHeader>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between h-full"
      >
        <section className="flex relative flex-col gap-4 px-4 md:px-6">
          {/* Produtos */}
          <MultiComboBox
            label={t("common:products")}
            placeholder={t("sales:select_products")}
            fetchOptions={fetchProductOptions}
            values={watch("products")}
            onChange={(value) => {
              handleProductsChange(value);
              clearErrors("products");
            }}
            helperText={errors.products?.message}
            queryKey="sold-products"
            returnFullObject
            cypressName="multicombobox-products"
            selectAllOptions
            newParams={[currency]}
          />

          {/* Ofertas */}
          <MultiComboBox
            key={`offers-${productIdsKey}`}
            label={t("common:offers")}
            placeholder={t("sales:select_offers")}
            fetchOptions={fetchOfferOptions}
            values={watch("offers")}
            onChange={(values) => {
              handleOffersChange(values);
              clearErrors("offers");
            }}
            helperText={errors.offers?.message}
            queryKey={queryKeys.offers}
            returnFullObject
            cypressName="multicombobox-offers"
            selectAllOptions
            newParams={productIds}
            disabled={productIds.length === 0}
          />

          <MultiComboBox
            key={`affiliates-${productIdsKey}`}
            label={t("sales:affiliate")}
            placeholder={t("sales:affiliate_placeholder")}
            fetchOptions={fetchAffiliatesOptions}
            values={watch("affiliates")}
            onChange={(value) => {
              setValue("affiliates", value);
              clearErrors("affiliates");
            }}
            helperText={errors.affiliates?.message}
            queryKey={queryKeys.affiliates}
            returnFullObject
            cypressName="multicombobox-affiliates"
            selectAllOptions
            newParams={productIds}
          />

          <Separator className="my-4" />

          {/* Métodos de Pagamento */}
          <PaymentMethodSelector
            variant="compact"
            availableMethods={soldPaymentMethods}
            selectedMethods={defaultValues.payment_methods ?? []}
            onChange={(methods) => {
              setValue('payment_methods', methods);
              clearErrors('payment_methods');
            }}
            emptyMessage={t('common:payment_methods_no_history')}
          />

          <Separator className="my-4" />

          {/* Status de Pagamento */}
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">{t("orders:table.payment_status")}</h2>
              <span
                className="text-sm font-medium text-primary hover:text-primary/80 cursor-pointer"
                onClick={() => handleSelectAll("status", Array.from(PAYMENT_STATUS))}
              >
                {t("common:select_all")}
              </span>
            </div>
            <StatusSelector
              options={PAYMENT_STATUS.map(status => ({ value: status, label: t(`common:${status}`) }))}
              selectedOptions={defaultValues.status ?? []}
              onChange={(statuses) => {
                setValue('status', statuses);
                clearErrors('status');
              }}
              variant="compact"
            />
          </div>

          <Separator className="my-4" />

          {/* Status de Processamento / Entrega */}
          <div className="space-y-4 mb-24">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">{t("orders:table.processing_status")}</h2>
              <span
                className="text-sm font-medium text-primary hover:text-primary/80 cursor-pointer"
                onClick={() => handleSelectAll("processing_status", Array.from(PROCESSING_STATUS))}
              >
                {t("common:select_all")}
              </span>
            </div>
            <StatusSelector
              options={PROCESSING_STATUS.map(status => ({ value: status, label: t(`orders:status.${status}`) }))}
              selectedOptions={defaultValues.processing_status ?? []}
              onChange={(statuses) => {
                setValue('processing_status', statuses);
                clearErrors('processing_status');
              }}
              variant="compact"
            />
          </div>
        </section>

        <SheetFooter className="flex z-50 sticky bottom-0 bg-[#09090b] w-full border-t border-t-colorBorder p-4 items-end mt-4 justify-end">
          <div className="flex w-full z-50 gap-2 justify-end items-end">
            <Button
              variant="ghost"
              className="w-full !text-colorText md:max-w-[118px]"
              onClick={() => closeModal(FILTER_ORDERS_MODAL)}
              type="button"
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant="default"
              className="w-full md:max-w-fit"
              type="submit"
              data-cy="apply-filters"
            >
              {t("sales:apply_filters")}
            </Button>
          </div>
        </SheetFooter>
      </form>
    </section>
  );
}
